import { useState, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { useAuthStore } from '@/stores/auth-store';
import { getChatApiUrl } from '../utils/constants';

const CHAT_API_URL = getChatApiUrl();
const MAX_IMAGES = 6;
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
const ACCEPTED_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
const ACCEPTED_EXTENSIONS = '.jpg,.jpeg,.png,.gif,.webp';

export interface UploadedImage {
  /** Unique client-side ID for keying */
  localId: string;
  /** Object URL for thumbnail preview (revoked on removal) */
  previewUrl: string;
  /** Original File object */
  file: File;
  /** Upload status */
  status: 'uploading' | 'done' | 'error';
  /** AI Designer imageId (set when upload completes) */
  imageId?: string;
}

let _localIdCounter = 0;

function readFileAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export function useImageUpload() {
  const { t } = useTranslation();
  const [images, setImages] = useState<UploadedImage[]>([]);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const openFilePicker = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const removeImage = useCallback((localId: string) => {
    setImages((prev) => {
      const img = prev.find((i) => i.localId === localId);
      if (img) URL.revokeObjectURL(img.previewUrl);
      return prev.filter((i) => i.localId !== localId);
    });
  }, []);

  const clearAllImages = useCallback(() => {
    setImages((prev) => {
      prev.forEach((img) => URL.revokeObjectURL(img.previewUrl));
      return [];
    });
  }, []);

  const uploadImage = useCallback(async (image: UploadedImage) => {
    const formData = new FormData();
    formData.append('images', image.file);

    try {
      const response = await fetch(`${CHAT_API_URL}/upload-image`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${useAuthStore.getState().token}`,
        },
        body: formData,
      });

      if (!response.ok) {
        throw new Error(`Upload failed: ${response.status}`);
      }

      const data = await response.json();
      const uploaded = data.images?.[0];

      if (!uploaded?.imageId) {
        throw new Error('No imageId in response');
      }

      setImages((prev) =>
        prev.map((img) =>
          img.localId === image.localId
            ? { ...img, status: 'done' as const, imageId: uploaded.imageId as string }
            : img,
        ),
      );
    } catch {
      setImages((prev) =>
        prev.map((img) =>
          img.localId === image.localId ? { ...img, status: 'error' as const } : img,
        ),
      );
    }
  }, []);

  const handleFileSelect = useCallback(
    (files: FileList | null) => {
      if (!files || files.length === 0) return;

      const available = MAX_IMAGES - images.length;
      if (available <= 0) {
        toast.error(t('optibot.image.maxReached'));
        return;
      }

      const filesToAdd = Array.from(files).slice(0, available);
      const newImages: UploadedImage[] = [];

      for (const file of filesToAdd) {
        if (!ACCEPTED_TYPES.includes(file.type)) {
          toast.error(t('optibot.image.invalidType', { name: file.name }));
          continue;
        }
        if (file.size > MAX_FILE_SIZE) {
          toast.error(t('optibot.image.tooLarge', { name: file.name }));
          continue;
        }

        _localIdCounter += 1;
        const localId = `img-${Date.now()}-${_localIdCounter}`;
        const previewUrl = URL.createObjectURL(file);

        newImages.push({ localId, previewUrl, file, status: 'uploading' });
      }

      if (newImages.length > 0) {
        setImages((prev) => [...prev, ...newImages]);
        newImages.forEach((img) => uploadImage(img));
      }

      // Reset input so the same file can be re-selected
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    },
    [images.length, uploadImage, t],
  );

  /** Get imageIds for all successfully uploaded images */
  const getUploadedImageIds = useCallback((): string[] => {
    return images
      .filter((img) => img.status === 'done' && img.imageId)
      .map((img) => img.imageId ?? '');
  }, [images]);

  /** Get FileUIParts for including in sendMessage (data URLs for Claude vision) */
  const getFileUIParts = useCallback(async () => {
    const parts = await Promise.all(
      images
        .filter((img) => img.status === 'done')
        .map(async (img) => {
          const dataUrl = await readFileAsDataUrl(img.file);
          return {
            type: 'file' as const,
            mediaType: img.file.type,
            filename: img.file.name,
            url: dataUrl,
          };
        }),
    );
    return parts;
  }, [images]);

  const hasImages = images.length > 0;
  const isUploading = images.some((img) => img.status === 'uploading');

  return {
    images,
    hasImages,
    isUploading,
    fileInputRef,
    acceptedExtensions: ACCEPTED_EXTENSIONS,
    openFilePicker,
    handleFileSelect,
    removeImage,
    clearAllImages,
    getUploadedImageIds,
    getFileUIParts,
  };
}
